// BHP-1191: reading the payment result, without a session.
//
// Its own HttpClient instance rather than the shared one, for three reasons
// that all come down to the same thing — there is no logged-in user here:
//
//  - no bearer token. The shared client reads one from the auth cookie, and
//    `ApiService` rejects outright when there is none.
//  - no `x-active-customer` header. There is no active customer, and sending
//    a stale one from a previous session would be worse than sending nothing.
//  - no `/api` prefix. The endpoint sits outside it on purpose: `/api/payments`
//    is already the Payment resource, where a static `result` path would get
//    in the way of `/api/payments/{uuid}` (BHP-1190).
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import env from '../../../config/env';
import { HttpClient } from '../../../shared/http/httpClient';
import { type PaymentResult } from '../payment';

const publicClient = new HttpClient({
  baseUrl: env.apiBaseUrl,
  getToken: () => null,
  getActiveCustomer: () => null
});

/**
 * How long a settled answer is served from cache. A paid or failed payment
 * does not change back, so re-asking costs a round trip for the same word.
 */
const SETTLED_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Poll while the answer is "not yet known", so the common case — a payment
 * confirmed a second or two after the redirect — turns into "paid" without
 * the customer doing anything.
 */
const PENDING_POLL_MS = 3000;

/**
 * And then stop. A bank transfer takes days, so this was never a wait that
 * resolves on screen; past a minute the page is just an abandoned tab calling
 * a public endpoint every three seconds for as long as it stays open. The
 * copy already tells the customer they will hear from us.
 */
const MAX_PENDING_POLLS = 20;

export function usePaymentResult(
  token: string | null
): UseQueryResult<PaymentResult> {
  return useQuery({
    queryKey: ['payment-result', token],
    queryFn: async () =>
      await publicClient.get<PaymentResult>('/payments/result', {
        params: { token: token ?? '' },
        // A plain JsonResponse, not an API Platform resource, so the default
        // `application/ld+json` would be asking for something it never sends.
        accept: 'application/json'
      }),
    enabled: token !== null && token !== '',
    staleTime: SETTLED_STALE_TIME_MS,
    refetchInterval: (query) =>
      query.state.data?.final === false &&
      query.state.dataUpdateCount < MAX_PENDING_POLLS
        ? PENDING_POLL_MS
        : false,
    // One attempt. The backend already answers rather than failing — an
    // unreachable Mollie comes back as "pending", not as an error — so a
    // failure here is our own API being down, and retrying behind a spinner
    // only makes the customer wait longer for the same message.
    retry: false
  });
}
